import { CircularProgress } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { PNG, SVG_MUI } from "@ryde/assets";
import { MuiButton } from "@ryde/controls";
import { useRtl } from "@ryde/redux";
import { useIsMobile } from "@ryde/shared";

import {
  CopyrightLine,
  LeftImageLogin,
  MainLoginContainerStyled,
} from "../login/login.page.styled";
import {
  McpConnectorActionFormStyled,
  McpConnectorActionTitleStyled,
} from "./parts/mcp-connector-action.styled";
import { useMcpConnectorAction } from "./parts/use-mcp-connector-action";

/**
 * Approval page for an AI-connector action that changes data in bulk or deletes it (RYDE-8447). The AI
 * client sends the user here ({spa}/#/mcp/action?action_id=...) and runs the action only once it is
 * approved. Lives outside ProtectedRoute: the session/login logic is owned by useMcpConnectorAction.
 */
export const McpConnectorActionPage: FC = () => {
  const { t } = useTranslation();
  const { direction } = useRtl();
  const { isMobile } = useIsMobile();
  const {
    pageState,
    errorMessage,
    decisionError,
    action,
    minutesLeft,
    statusMessageKey,
    details,
    isSubmitting,
    isNonProductionLoginColors,
    handleApprove,
    handleDeny,
  } = useMcpConnectorAction();

  return (
    <MainLoginContainerStyled dir={direction}>
      {!isMobile && (
        <LeftImageLogin>
          <SVG_MUI.LogoLoginSVG className="logo" />
          <img src={PNG.LoginLogoPNG} alt="logo" />
        </LeftImageLogin>
      )}

      <McpConnectorActionFormStyled>
        <div className="actionCard">
          {/* The one page heading, in every state, so assistive technology always has a title to land on. */}
          <McpConnectorActionTitleStyled
            $isNonProductionLoginColors={isNonProductionLoginColors}
          >
            <span className="login-title-row">
              <span className="login-title-primary">
                {t("mcpConnectorAction.title")}
              </span>
            </span>
          </McpConnectorActionTitleStyled>

          {/* Live regions: state changes are announced to assistive technology. */}
          {pageState === "loading" && (
            <div
              className="statusMessage"
              role="status"
              aria-live="polite"
              aria-label={t("general.loading")}
            >
              <CircularProgress size={28} />
            </div>
          )}

          {pageState === "error" && (
            <div className="errorMessage" role="alert">
              {errorMessage}
            </div>
          )}

          {(pageState === "ready" || pageState === "decided") && action && (
            <>
              <p className="requestedBy">
                {t("mcpConnectorAction.requestedBy", {
                  clientName: action.clientName,
                  companyName: action.companyName,
                })}
              </p>
              <h2 className="summaryTitle">{action.summary.title}</h2>
              {action.summary.lines.length > 0 && (
                <ul className="summaryLines">
                  {action.summary.lines.map((line, index) => (
                    // Lines are free text and may repeat, so the position is part of the key.
                    <li key={`${index}-${line}`}>{line}</li>
                  ))}
                </ul>
              )}

              <details className="details">
                <summary>{t("mcpConnectorAction.technicalDetails")}</summary>
                {/* A request is code: it reads left to right in Hebrew too. The attribute, not CSS, because the RTL
                    style plugin flips a CSS direction. */}
                <pre dir="ltr">{details}</pre>
              </details>

              {pageState === "ready" && (
                <>
                  {minutesLeft > 0 && (
                    <p className="timeLeft">
                      {t("mcpConnectorAction.timeLeft", {
                        minutes: minutesLeft,
                      })}
                    </p>
                  )}
                  <p className="nothingChangesYet">
                    {t("mcpConnectorAction.nothingChangesYet", {
                      clientName: action.clientName,
                    })}
                  </p>

                  {Boolean(decisionError) && (
                    <div className="errorMessage" role="alert">
                      {decisionError}
                    </div>
                  )}

                  <div className="actions">
                    <MuiButton
                      muiVariant="outlined"
                      muiStyle="secondary"
                      onClick={handleDeny}
                      disabled={isSubmitting}
                      e2eData="mcp-connector-action-deny"
                    >
                      {t("mcpConnectorAction.deny")}
                    </MuiButton>
                    <MuiButton
                      onClick={handleApprove}
                      loading={isSubmitting}
                      e2eData="mcp-connector-action-approve"
                    >
                      {t("mcpConnectorAction.approve")}
                    </MuiButton>
                  </div>
                </>
              )}

              {pageState === "decided" && statusMessageKey && (
                <div className="statusMessage" role="status" aria-live="polite">
                  {t(statusMessageKey, { clientName: action.clientName })}
                </div>
              )}
            </>
          )}
        </div>
      </McpConnectorActionFormStyled>

      {!isMobile && (
        <CopyrightLine>
          <div>{t("ui.COPYRIGHT_INFO")} </div>
        </CopyrightLine>
      )}
    </MainLoginContainerStyled>
  );
};
